import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject
} from "@angular/core";
import {
  EventModel,
  Scheduler,
  SchedulerFeaturesConfigType
} from "@bryntum/scheduler-thin";
import { StringHelper } from "@bryntum/core-thin";
import moment, { Moment } from "moment";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TicketDrag } from "@app/view/planning/schedule-viewer/ticket-drag";
import { Router } from "@angular/router";
import { ScheduleLayout } from "@app/core/schedule/schedule-layout";
import { IScheduleViewer } from "@app/core/schedule/schedule-viewer.interface";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import { TicketCompactRenderer } from "@app/view/planning/schedule-viewer/ticket-compact-renderer";
import { ScheduleProjectService } from "@app/services/project/schedule-project.service";
import { SchedulerService } from "@app/view/planning/schedule-viewer/scheduler.service";
import { ScheduleDefaults } from "@app/view/planning/schedule-viewer/schedule-defaults";
import { PropertyService } from "@app/integrations/urm/services/property.service";
import { JobService } from "@app/integrations/urm/services/job.service";
import { LiveMapComponent } from "@app/view/planning/live-map/live-map.component";
import { SplitAreaComponent, SplitComponent } from "angular-split";
import { TicketEditDialogComponent } from "@app/view/planning/ticket-edit-dialog/ticket-edit-dialog.component";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { ResourceDetailComponent } from "@app/view/planning/resource-detail/resource-detail.component";
import { Ticket_DetailProjection } from "@app/models/rest/ticket";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { TicketStatus } from "@app/models/enums/ticket-status.enum";
import { TicketDespatchDialogComponent } from "@app/view/planning/ticket-despatch-dialog/ticket-despatch-dialog.component";
import { TicketRecallDialogComponent } from "@app/view/planning/ticket-recall-dialog/ticket-recall-dialog.component";
import { TicketRecallService } from "@app/services/http/ticket-recall.service";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { MeetingActionDialogComponent } from "@app/view/planning/meeting-action-dialog/meeting-action-dialog.component";
import { PropPropertyHeader } from "@app/integrations/urm/models/prop-property-header";
import { LiveMapComponent as LiveMapComponent_1 } from "../live-map/live-map.component";
import { MatIcon } from "@angular/material/icon";
import { NgxUiLoaderModule } from "ngx-ui-loader";
import { SkillStore } from "@app/services/store/skill-store";
import { ZoneStore } from "@app/services/store/zone-store";
import { TeamStore } from "@app/services/store/team-store";
import { ResourceStore } from "@app/services/store/resource-store";
import { ConfirmDialogComponent } from "@app/view/shared/confirm-dialog/confirm-dialog.component";
import { AvailabilityType } from "@app/models/enums/availability-type.enum";
import { ResourceAvailability } from "@app/models/rest/resource-availability";

@Component({
  selector: "app-schedule-viewer",
  styleUrls: ["./schedule-viewer.component.scss"],
  templateUrl: "./schedule-viewer.component.html",
  imports: [
    SplitComponent,
    SplitAreaComponent,
    LiveMapComponent_1,
    MatIcon,
    NgxUiLoaderModule
  ]
})
export class ScheduleViewerComponent
  implements OnInit, OnDestroy, IScheduleViewer
{
  protected element = inject(ElementRef);
  protected router = inject(Router);
  protected dialog = inject(MatDialog);
  scheduleProjectService = inject(ScheduleProjectService);
  protected schedulerService = inject(SchedulerService);
  protected ticketRender = inject(TicketCompactRenderer);
  protected ticketDrag = inject(TicketDrag);
  protected propertyService = inject(PropertyService);
  protected jobService = inject(JobService);
  protected ticketStore = inject(TicketStreamingStore);
  protected recallService = inject(TicketRecallService);
  protected snackBar = inject(MatSnackBar);
  config = inject(ClientConfigService);
  private store = inject<Store<any>>(Store);
  protected skillStore = inject(SkillStore);
  protected zoneStore = inject(ZoneStore);
  protected teamStore = inject(TeamStore);
  protected resourceStore = inject(ResourceStore);

  public initStartDate: Date = null;
  public initEndDate: Date = null;
  public error: boolean = false;
  public initialized: boolean = false;
  @Input()
  public showMap: boolean = false;
  projectLoaded: boolean = false;

  @Output() public layoutChange = new EventEmitter<boolean>();
  @Output() public initialised = new EventEmitter<boolean>();
  mapWidth: number = 50;
  scheduleWidth: number = 50;
  public newMapWidth: number;
  private tooltip: HTMLElement;
  W;
  @ViewChild("liveMap", { static: false })
  private liveMap: LiveMapComponent;
  @ViewChild(SplitComponent, { static: false })
  private splitEl: SplitComponent;
  private allowResourceLocking: boolean;
  private allowForceBooking: boolean;
  private activeOnlyFilter: boolean = false;
  private activeOnlyListenersAttached: boolean = false;
  private static readonly ACTIVE_ONLY_FILTER_ID = "active-only-filter";

  // No longer need fake stores - using direct array filtering

  constructor() {
    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.allowResourceLocking = !!auth && auth.allowResourceLocking;
      this.allowForceBooking = !!auth && auth.allowForceBooking;
    });
  }

  ngOnInit() {
    // Load skills and zones data before scheduler initialization
    this.loadFilterData();
  }

  private async loadFilterData() {
    try {
      // Load skills, zones, teams, and resources in parallel
      const [skillsResult, zonesResult, teamsResult, resourcesResult] =
        await Promise.all([
          this.skillStore.loadAll(),
          this.zoneStore.loadAll(),
          this.teamStore.loadAll(),
          this.resourceStore.loadAll()
        ]);

      console.log("Skills loaded:", skillsResult);
      console.log("Zones loaded:", zonesResult);
      console.log("Teams loaded:", teamsResult);
      console.log("Resources loaded:", resourcesResult);

      // Set the filter data in ScheduleDefaults before scheduler creation
      ScheduleDefaults.setFilterData(
        skillsResult.resources || [],
        zonesResult.resources || [],
        teamsResult.resources || [],
        resourcesResult.resources || []
      );

      console.log("Filter data set in ScheduleDefaults");
    } catch (error) {
      console.error("Error loading filter data:", error);
    }
  }

  ngOnDestroy(): void {
    if (this.schedulerService.scheduler.getValue()) {
      this.schedulerService.scheduler.getValue().destroy();
      this.schedulerService.updateScheduler(null);
    }

    // Remove tooltip if it exists
    this.removeTooltip();
  }

  /**
   * Run a lock-changing action from the context menu and then refresh the
   * ticket so the icons and menu reflect the new state. On failure, surface a
   * message and still refresh, so the UI shows the true server state rather
   * than going stale (see issue #905).
   */
  private runLockAction(
    ticketId: number,
    action: Promise<unknown>,
    failureMessage: string
  ): void {
    action
      .catch((err) => {
        console.error(failureMessage, err);
        this.snackBar.open(failureMessage, "Close", { duration: 5000 });
      })
      .finally(() => this.refreshScheduleTicket(ticketId));
  }

  /**
   * Refresh a single ticket's event record, swallowing (but logging) any
   * failure so it never surfaces as an unhandled promise rejection.
   */
  private refreshScheduleTicket(ticketId: number): void {
    this.scheduleProjectService.refreshTicket(ticketId).catch((err) => {
      console.error("Failed to refresh ticket on schedule", err);
    });
  }

  private createTooltip(): void {
    // Remove any existing tooltip
    this.removeTooltip();

    // Create tooltip element
    this.tooltip = document.createElement("div");
    this.tooltip.innerHTML = "hello";
    this.tooltip.style.position = "absolute";
    this.tooltip.style.backgroundColor = "white";
    this.tooltip.style.color = "black";
    this.tooltip.style.padding = "5px 8px";
    this.tooltip.style.borderRadius = "4px";
    this.tooltip.style.boxShadow = "0 2px 5px rgba(0, 0, 0, 0.2)";
    this.tooltip.style.zIndex = "10000";
    this.tooltip.style.display = "none";

    // Add tooltip to the document body
    document.body.appendChild(this.tooltip);
  }

  private removeTooltip(): void {
    if (this.tooltip && this.tooltip.parentNode) {
      this.tooltip.parentNode.removeChild(this.tooltip);
      this.tooltip = null;
    }
  }

  private updateTooltipPosition(event: MouseEvent): void {
    if (this.tooltip) {
      // Position tooltip near the mouse pointer
      this.tooltip.style.left = `${event.clientX + 10}px`;
      this.tooltip.style.top = `${event.clientY + 10}px`;
    }
  }

  private showTooltip(): void {
    if (this.tooltip) {
      this.tooltip.style.display = "none"; //TODO: finish this tooltip
    }
  }

  private hideTooltip(): void {
    if (this.tooltip) {
      this.tooltip.style.display = "none";
    }
  }

  splitterDragStart($event) {
    console.log("drag start", $event);
  }

  splitterDragEnd($event) {
    console.log("drag end", $event);
    this.liveMap.updateMapSize();
    this.newMapWidth = $event.sizes[1];
    this.layoutChange.emit(true);
  }

  applyScheduleLayout(scheduleLayout: ScheduleLayout) {
    console.log("applying layout", scheduleLayout);
    this.setActiveOnly(scheduleLayout?.activeOnly === true);

    // return;
    if (!scheduleLayout || scheduleLayout.columns === undefined) {
      console.log("no layout supplied");
      this.schedulerService.scheduler.getValue()?.refreshWithTransition();
      return;
    }
    if (scheduleLayout.mapWidth) {
      console.log("setting map width to", scheduleLayout.mapWidth);
      this.mapWidth = Math.round(scheduleLayout.mapWidth);
      this.scheduleWidth = 100 - Math.round(scheduleLayout.mapWidth);
    }
    if (this.initialized) {
      if (this.liveMap) {
        this.liveMap.updateMapSize();
      }
    } else {
      let initSubscription = this.initialised.subscribe(() => {
        initSubscription.unsubscribe();
        this.applyScheduleLayout(scheduleLayout);
        if (this.liveMap) {
          this.liveMap.updateMapSize();
        }
      });
      // console.log('not yet initialised');
    }
  }

  // <editor-fold desc="Initialization">
  getScheduleLayout(): ScheduleLayout {
    const layout = new ScheduleLayout();
    layout.mapWidth = this.newMapWidth;
    layout.activeOnly = this.activeOnlyFilter;
    return layout;
  }

  setDate(date: Moment, startHour: number, hourCount: number) {
    console.log(
      `Setting view to date=${date}, startHour=${startHour}, hourCount=${hourCount}`
    );
    const newStart = date.startOf("day").add(startHour, "hours").local(true);
    const newEnd = moment(newStart).add(hourCount, "hours").local(true);

    if (
      newStart.isSame(this.initStartDate) &&
      newEnd.isSame(this.initEndDate)
    ) {
      //nothing to do
      return;
    }
    this.initStartDate = newStart.parseZone().toDate();
    this.initEndDate = newEnd.parseZone().toDate();

    this.scheduleProjectService.initStartDate = this.initStartDate;
    this.scheduleProjectService.initEndDate = this.initEndDate;

    // this.dataLoading = true;
    if (this.schedulerService.scheduler.getValue()) {
      this.schedulerService.scheduler
        .getValue()
        .setTimeSpan(this.initStartDate, this.initEndDate);
      this.scheduleProjectService.loadAvailabilities();
      // Skills/zones shown per resource depend on the view range (#1558).
      this.scheduleProjectService.refreshEffectiveResourceFacts();
      this.applyActiveOnlyFilter();
    }
  }

  public setActiveOnly(activeOnly: boolean) {
    if (this.activeOnlyFilter === activeOnly) {
      return;
    }
    this.activeOnlyFilter = activeOnly;
    this.applyActiveOnlyFilter();
  }

  public isActiveOnly(): boolean {
    return this.activeOnlyFilter;
  }

  private applyActiveOnlyFilter() {
    const scheduler = this.schedulerService.scheduler.getValue();
    const project = this.scheduleProjectService.projectData.getValue();
    const resourceStore = scheduler?.resourceStore ?? project?.resourceStore;
    if (!resourceStore) {
      return;
    }

    if (!this.activeOnlyFilter) {
      resourceStore.removeFilter(ScheduleViewerComponent.ACTIVE_ONLY_FILTER_ID);
      return;
    }

    const resourceTimeRangeStore: any =
      scheduler?.resourceTimeRangeStore ?? project?.resourceTimeRangeStore;
    if (!resourceTimeRangeStore) {
      return;
    }

    const resourceIdsWithPositiveShifts = new Set<any>();
    resourceTimeRangeStore.records.forEach((range: any) => {
      const rangeStart = range.startDate ?? range.get?.("startDate");
      const rangeEnd = range.endDate ?? range.get?.("endDate");
      if (!this.isTimeSpanInCurrentView(rangeStart, rangeEnd)) {
        return;
      }

      const entity = range.get
        ? (range.get("entity") as ResourceAvailability)
        : (range.entity as ResourceAvailability);
      const isPositive =
        !entity ||
        !entity.availabilityCode ||
        entity.availabilityCode.availabilityType === AvailabilityType.ASSIGN;
      if (!isPositive) {
        return;
      }

      const resourceId = range.resourceId ?? range.get?.("resourceId");
      if (resourceId !== undefined && resourceId !== null) {
        resourceIdsWithPositiveShifts.add(resourceId);
      }
    });

    resourceStore.filter({
      id: ScheduleViewerComponent.ACTIVE_ONLY_FILTER_ID,
      filterBy: (resource: any) =>
        resourceIdsWithPositiveShifts.has(resource.id)
    });
  }

  private isTimeSpanInCurrentView(startDate: Date, endDate: Date): boolean {
    if (!startDate || !endDate || !this.initStartDate || !this.initEndDate) {
      return false;
    }
    return startDate < this.initEndDate && endDate > this.initStartDate;
  }

  private attachActiveOnlyListeners(scheduler: Scheduler) {
    if (this.activeOnlyListenersAttached) {
      return;
    }
    scheduler.on("timeAxisChange", this.applyActiveOnlyFilter.bind(this));
    (scheduler.resourceTimeRangeStore as any).on(
      "change",
      this.applyActiveOnlyFilter.bind(this)
    );
    this.activeOnlyListenersAttached = true;
  }

  performInit() {
    if (this.initialized) {
      console.log("Already initialised");
      return;
    }
    if (!this.scheduleProjectService.initialized) {
      this.scheduleProjectService.loadProject().then();
    }

    console.log("running init");
    const config = {
      startDate: this.initStartDate,
      endDate: this.initEndDate,
      insertFirst:
        this.element.nativeElement.firstElementChild.firstElementChild,
      eventRenderer: this.ticketRender.renderEvent,
      eventRendererThisObj: this.ticketRender,

      enableEventAnimations: !this.config.config.disableAnimation,
      useInitialAnimation: !this.config.config.disableAnimation,
      project: null,
      listeners: {
        eventDrop: this.ticketDrag.eventDrop,
        eventDragStart: this.ticketDrag.eventDragStart,
        eventDragAbort: this.ticketDrag.eventDragAbort,
        beforeRenderRow: ({ row, record }) => {
          if (record.get("highlightRule")) {
            row.cls.add("activity-row");
          } else {
            row.cls.remove("activity-row");
          }
        },
        beforeEventDropFinalize: this.ticketDrag.beforeEventDropFinalize,
        beforeEventDrag: this.ticketDrag.beforeEventDrag,
        cellClick: ({ record, column }) => {
          console.log("event");
          if (column.type === "resourceInfo") {
            console.log(record);
            if (!record.meta.specialRow && record.data.entity) {
              console.log(column);
              this.dialog.open(ResourceDetailComponent, {
                data: {
                  resource: record.data.entity,
                  dateFrom: this.initStartDate,
                  dateTo: this.initEndDate
                },
                width: "calc(100vw-100px)",
                height: "calc(100vh-100px)"
              });
            }
          }
        },
        eventClick: ({ eventRecord, event }) => {
          let targetY = Math.min(
            event.clientY,
            event.view.window.innerHeight - 555
          );
          let targetX = Math.min(
            event.clientX,
            event.view.window.innerWidth - 502
          );
          if (eventRecord.get("meetingId")) {
            this.dialog.open(MeetingActionDialogComponent, {
              hasBackdrop: false,
              autoFocus: false,
              width: "550px",
              position: { top: targetY + "px", left: targetX + "px" },
              panelClass: "toolbar-popup-dialog",
              data: { ticketId: eventRecord.id }
            });
          } else {
            this.dialog.open(TicketActionDialogComponent, {
              hasBackdrop: false,
              autoFocus: false,
              width: "500px",
              position: { top: targetY + "px", left: targetX + "px" },
              panelClass: "toolbar-popup-dialog",
              data: { ticketId: eventRecord.id }
            });
          }
        },
        // Add our resource time range event handlers
        resourceTimeRangeMouseOver: ({ resourceTimeRangeRecord, domEvent }) => {
          // Create tooltip when hovering over a resource time range
          console.log("resourceTimeRangeMouseOver", resourceTimeRangeRecord);
          this.createTooltip();
          this.updateTooltipPosition(domEvent);
          this.showTooltip();
        },
        resourceTimeRangeMouseMove: ({ event }) => {
          // Update tooltip position as mouse moves
          console.log("resourceTimeRangeMouseMove", event);
          this.updateTooltipPosition(event);
        },
        resourceTimeRangeMouseOut: () => {
          // Hide tooltip when mouse leaves the resource time range
          this.hideTooltip();
        },
        thisObj: this
      }
    };
    const features: Partial<SchedulerFeaturesConfigType> = {
      eventMenu: {
        items: {
          viewTicket: {
            text: "View Ticket",
            icon: "fa fa-info",
            weight: 200,
            onItem: ({ eventRecord }) => {
              if (eventRecord.get("meetingId")) {
                this.dialog.open(MeetingActionDialogComponent, {
                  hasBackdrop: true,
                  width: "550px",
                  panelClass: "toolbar-popup-dialog",
                  data: {
                    ticketId: eventRecord.get("id")
                  }
                });
              } else {
                this.dialog.open(TicketActionDialogComponent, {
                  hasBackdrop: true,
                  width: "500px",
                  panelClass: "toolbar-popup-dialog",
                  data: {
                    ticketId: eventRecord.get("id")
                  }
                });
              }
            }
          },
          manualPlanning: {
            text: "Manual Planning",
            icon: "fa fa-user-clock",
            weight: 200,
            onItem: ({ eventRecord }) => {
              const ticketId = eventRecord.get("id");
              this.dialog
                .open(TicketEditDialogComponent, {
                  width: "400px",
                  data: {
                    ticketId: ticketId,
                    newForceStartTime: eventRecord.get("forceStartTime")
                      ? moment(eventRecord.get("forceStartTime"))
                      : moment(eventRecord.get("startTime")),
                    oldResourceId: eventRecord.get("resourceId"),
                    newResourceId: eventRecord.get("resourceId")
                  },
                  panelClass: "toolbar-popup-dialog"
                })
                .afterClosed()
                .subscribe((result) => {
                  // A lock change made via the dialog may not re-position the
                  // ticket (and a resource-lock change never emits a ticket
                  // update event), so refresh the event record explicitly to
                  // keep the icons and context menu in sync. See issue #905.
                  if (result) {
                    this.refreshScheduleTicket(
                      result.refreshTicket ?? ticketId
                    );
                  }
                });
            }
          },
          manualDespatch: {
            text: "Manually Despatch Ticket",
            icon: "fa fa-paper-plane",
            weight: 200,
            onItem: ({ eventRecord }) => {
              let despatchRef = this.dialog.open(TicketDespatchDialogComponent);
              despatchRef.afterClosed().subscribe((value) => {
                if (value) {
                  this.ticketStore
                    .loadSingle(eventRecord.get("id"))
                    .then((value1) => {
                      this.ticketStore
                        .setTicketStatus(value1, TicketStatus.DESPATCHED)
                        .then();
                    });
                }
              });
            }
          },
          removeLock: {
            text: "Remove Time Lock",
            icon: "fa fa-xmark",
            weight: 200,
            onItem: ({ eventRecord }) => {
              const ticketId = eventRecord.get("id");
              this.runLockAction(
                ticketId,
                this.ticketStore.forceTicketStart(ticketId, null),
                "Failed to remove time lock"
              );
            }
          },
          removeResourceLock: {
            text: "Remove Resource Lock",
            icon: "fa fa-user-xmark",
            weight: 200,
            onItem: ({ eventRecord }) => {
              const ticketId = eventRecord.get("id");
              this.runLockAction(
                ticketId,
                this.ticketStore.lockResourceAsync(ticketId, null),
                "Failed to remove resource lock"
              );
            }
          },
          recallTicket: {
            text: "Recall Ticket",
            icon: "fa fa-rotate-left",
            weight: 200,
            onItem: async ({ eventRecord }) => {
              // Defensive fallback: the menu item is hidden in processItems
              // when the ticket is grouped (multi-ticket visit), but if that
              // flag is stale verify the visit's real ticket count and warn
              // the user rather than proceeding.
              if (this.config.config.disableTwoPersonTicketRecall === true) {
                try {
                  const ticketDetail =
                    await this.ticketStore.loadSingleProjection<Ticket_DetailProjection>(
                      Ticket_DetailProjection,
                      eventRecord.get("id") as number,
                      "detail"
                    );
                  const ticketCount =
                    ticketDetail?.visit?.ticketList?.length ?? 1;
                  if (ticketCount > 1) {
                    this.dialog.open(ConfirmDialogComponent, {
                      data: {
                        title: "Recall not available",
                        message:
                          "This visit has more than one ticket (two-person working). Recall is disabled in this configuration.",
                        showCancelButton: false
                      }
                    });
                    return;
                  }
                } catch (err) {
                  console.warn(
                    "Failed to verify ticket count before recall",
                    err
                  );
                  this.dialog.open(ConfirmDialogComponent, {
                    data: {
                      title: "Recall unavailable",
                      message:
                        "Unable to verify recall eligibility. Please try again.",
                      showCancelButton: false
                    }
                  });
                  return;
                }
              }
              let recallRef = this.dialog.open(TicketRecallDialogComponent);
              recallRef.afterClosed().subscribe((value) => {
                if (value) {
                  this.recallService
                    .initiateRecall(eventRecord.get("id"))
                    .then(() => {
                      this.snackBar.open(
                        "Recall initiated successfully",
                        "Close",
                        { duration: 5000 }
                      );
                    });
                }
              });
            }
          },
          recallDisabled: {
            text: "Recall Disabled",
            icon: "fa fa-ban",
            weight: 201,
            disabled: true,
            hidden: true
          },
          deleteEvent: false
        },
        processItems: ({ eventRecord, items }) => {
          console.log("Preparing context record for", eventRecord);
          if (eventRecord.get("meetingId") !== null) {
            // @ts-ignore
            items.manualPlanning.hidden = true;
          }
          if (!this.allowForceBooking) {
            // @ts-ignore
            items.manualPlanning.hidden = true;
          }
          // @ts-ignore
          items.removeLock.hidden = false;
          if (
            eventRecord.get("forceStartTime") === null ||
            eventRecord.get("meetingId") !== null ||
            !this.allowForceBooking
          ) {
            console.log("Hiding remove lock", eventRecord);
            // @ts-ignore
            items.removeLock.hidden = true;
            if (
              this.allowForceBooking &&
              eventRecord.get("meetingId") === null
            ) {
              // @ts-ignore
              items.manualPlanning.hidden = false;
            }
          }
          // @ts-ignore
          items.removeResourceLock.hidden = false;
          if (
            eventRecord.get("lockResourceId") === null ||
            !this.allowResourceLocking ||
            eventRecord.get("meetingId") !== null
          ) {
            console.log("Hiding remove resource lock", eventRecord);
            // @ts-ignore
            items.removeResourceLock.hidden = true;
          }
          if (
            !eventRecord.get("canDespatch") ||
            !moment(eventRecord.get("startDate")).isSame(moment(), "day")
          ) {
            // @ts-ignore
            items.manualDespatch.hidden = true;
          }
          // Show recall only for DESPATCHED or ENROUTE tickets that are not meetings
          const ticketStatus = eventRecord.get("ticketStatus");
          // @ts-ignore
          items.recallTicket.hidden = true;
          // @ts-ignore
          items.recallDisabled.hidden = true;
          if (
            !eventRecord.get("meetingId") &&
            (ticketStatus === "DESPATCHED" || ticketStatus === "ENROUTE") &&
            !eventRecord.get("recallPending")
          ) {
            // groupedTicketId is set server-side when a ticket belongs to a
            // multi-ticket visit (two-person working), so use it as the sync
            // gate. A click-time fallback in onItem re-verifies by loading
            // the visit's ticket list in case this flag is stale.
            const recallDisabled =
              this.config.config.disableTicketRecall === true;
            const twoPersonRecallDisabled =
              this.config.config.disableTwoPersonTicketRecall === true &&
              !!eventRecord.get("groupedTicketId");
            if (recallDisabled || twoPersonRecallDisabled) {
              // @ts-ignore
              items.recallDisabled.hidden = false;
              // @ts-ignore
              items.recallDisabled.text = twoPersonRecallDisabled
                ? "Recall Disabled (two-person working)"
                : "Recall Disabled";
            } else {
              // @ts-ignore
              items.recallTicket.hidden = false;
            }
          }
        }
      },
      eventTooltip: {
        allowOver: true,
        template: async ({ eventRecord }) => {
          function truncateText(text: string, maxLength: number = 120): string {
            if (!text || text.length <= maxLength) return text;
            return text.substring(0, maxLength) + "\u2026";
          }

          function formatPropertyDetails(property: PropPropertyHeader): string {
            const addressFields = [
              property.Proh_PropertyName,
              property.Proh_Address1,
              property.Proh_Address2,
              property.Proh_Address3,
              property.Proh_Address4,
              property.Proh_PostCode
            ];

            return addressFields
              .filter((field) => field) // Remove undefined or empty fields
              .map((field) => `${StringHelper.encodeHtml(field)}<br/>`)
              .join("");
          }

          function formatAlerts(record: any): string {
            const issues: any[] = record.get("issues");
            if (!issues || issues.length === 0) {
              return "";
            }
            const issueItems = issues
              .map(
                (issue) =>
                  `<div style="display: flex; align-items: center; gap: 4px;">
                    <span style="color: ${issue.hard ? "#d32f2f" : "#f57c00"};">&#9888;</span>
                    <span>${StringHelper.encodeHtml(issue.rule)}</span>
                  </div>`
              )
              .join("");
            return `
              <div style="
                font-size: 11px;
                margin-top: 10px;
                padding: 8px;
                border: 1px solid ${issues.some((i) => i.hard) ? "#d32f2f" : "#f57c00"};
                border-radius: 10px;
                background-color: ${issues.some((i) => i.hard) ? "#ffebee" : "#fff3e0"};
                color: #333;">
                <div style="font-weight: bold; margin-bottom: 4px;">Planning Alerts</div>
                ${issueItems}
              </div>`;
          }

          function formatPriorityBlock(job: any): string {
            if (!job?.priority) {
              return "";
            }
            return `
              <div style="
                padding: 10px;
                background-color: ${job.priority.backgroundColor};
                color: ${job.priority.foregroundColor};
                border-radius: 10px;
                max-width: 150px;
                border: 1px solid black;">
                  <span style="font-size: 14px; line-height: 14px;">${StringHelper.encodeHtml(
                    job.priority.description
                  )}</span><br/>
                  <span style="font-size: 12px; line-height: 12px;">Due ${moment(
                    job.planningWindowEnd
                  ).format("DD/MM/YYYY HH:mm")}</span>
              </div>`;
          }

          function coreUnavailableNotice(): string {
            return `
              <div style="
                font-size: 11px;
                margin-top: 10px;
                padding: 8px;
                border: 1px solid #f57c00;
                border-radius: 10px;
                background-color: #fff3e0;
                color: #333;">
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="color: #f57c00;">&#9888;</span>
                  <span>Unable to contact Core. Showing Resolve information only.</span>
                </div>
              </div>`;
          }

          function formatHazardCaseNotice(job: any): string {
            if (!job?.externalCaseReference) {
              return "";
            }
            return `
              <div style="
                font-size: 11px;
                margin-top: 10px;
                padding: 8px;
                border: 1px solid #f57c00;
                border-radius: 10px;
                background-color: #fff3e0;
                color: #333;">
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="color: #f57c00;">&#9888;</span>
                  <span>Hazard Case ${StringHelper.encodeHtml(
                    job.externalCaseReference
                  )}</span>
                </div>
              </div>`;
          }

          try {
            const ticket =
              await this.ticketStore.loadSingleProjection<Ticket_DetailProjection>(
                Ticket_DetailProjection,
                eventRecord.id,
                "detail"
              );

            const hasJob =
              ticket.visit.job !== null && ticket.visit.job !== undefined;

            // Tooltip built only from data held in Resolve, used when the
            // Core/URM services are disabled or cannot be reached.
            const resolveOnlyDetails = (coreFailed: boolean): string => {
              if (hasJob) {
                return `
              <div style="max-height: 300px; overflow-y: auto;">
              <span style="font-size: 120%; font-weight: bolder">${StringHelper.encodeHtml(
                ticket.visit.job.externalJobReference
              )}</span><br/>
              ${moment(ticket.enrouteTime).parseZone().format("HH:mm")} -
              ${moment(ticket.completedTime).parseZone().format("HH:mm")}<br/>
              ${formatPriorityBlock(ticket.visit.job)}
              ${formatHazardCaseNotice(ticket.visit.job)}
              ${coreFailed ? coreUnavailableNotice() : ""}
              ${formatAlerts(eventRecord)}
              </div>
            `;
              }
              const meetingDescription = ticket.visit.meeting?.description;
              return `
            <div style="max-height: 300px; overflow-y: auto;">
            ${
              meetingDescription
                ? StringHelper.encodeHtml(truncateText(meetingDescription))
                : "No details available."
            }
            ${coreFailed ? coreUnavailableNotice() : ""}
            ${formatAlerts(eventRecord)}
            </div>
            `;
            };

            if (this.config.config.urmServices.enabled) {
              const propertyRef = hasJob
                ? ticket.visit.job.externalJobReference
                : ticket.visit.meeting?.externalPropertyReference;

              if (propertyRef) {
                try {
                  if (hasJob) {
                    // Load job details
                    const job = await this.jobService.loadJobById(propertyRef);
                    // Load property details associated with the job
                    const property =
                      await this.propertyService.loadPropertyById(
                        ticket.visit.job.externalPropertyReference
                      );

                    // Construct job-related HTML
                    return `
              <div style="max-height: 300px; overflow-y: auto;">
              <span style="font-size: 120%; font-weight: bolder">${StringHelper.encodeHtml(
                job.Jobf_JobId
              )}</span><br/>
              ${StringHelper.encodeHtml(
                truncateText(job.Jobf_JobDescription)
              )}<br/>
              ${moment(ticket.enrouteTime).parseZone().format("HH:mm")} -
              ${moment(ticket.completedTime).parseZone().format("HH:mm")}<br/>
              ${formatPriorityBlock(ticket.visit.job)}
              ${formatHazardCaseNotice(ticket.visit.job)}
              <div style="
                font-size: 10px;
                margin-top: 10px;
                line-height: 12px;
                padding: 10px;
                border: 1px solid black;
                border-radius: 10px;
                background-color: grey;">
                  ${formatPropertyDetails(property)}
              </div>
              ${formatAlerts(eventRecord)}
              </div>
            `;
                  } else {
                    // Load property details associated with the meeting
                    const property =
                      await this.propertyService.loadPropertyById(propertyRef);

                    // Construct meeting-related HTML
                    return `
            <div style="max-height: 300px; overflow-y: auto;">
            ${StringHelper.encodeHtml(
              truncateText(ticket.visit.meeting.description)
            )}
              <div style="
                font-size: 10px;
                margin-top: 10px;
                line-height: 12px;
                padding: 10px;
                border: 1px solid black;
                border-radius: 10px;
                background-color: grey;">
                  ${formatPropertyDetails(property)}
              </div>
              ${formatAlerts(eventRecord)}
            </div>
            `;
                  }
                } catch (coreError) {
                  // Core/URM unreachable — show what Resolve holds and flag
                  // that the live Core details could not be fetched.
                  console.error(
                    "Error loading Core details for tooltip:",
                    coreError
                  );
                  return resolveOnlyDetails(true);
                }
              } else {
                // If there's no property reference, fallback to descriptions
                const fallback = hasJob
                  ? ticket.visit.job.externalJobReference
                  : ticket.visit.meeting?.description ||
                    "No details available.";
                return (
                  StringHelper.encodeHtml(fallback) + formatAlerts(eventRecord)
                );
              }
            } else {
              // URM services not enabled — show what Resolve holds, without
              // the Core warning since no Core call was expected.
              return resolveOnlyDetails(false);
            }
          } catch (error) {
            console.error("Error loading tooltip:", error);
            return "Unable to load details." + formatAlerts(eventRecord);
          }
        }
      },
      eventDrag: this.ticketDrag.config,
      resourceTimeRanges: {
        enableMouseEvents: true
      }
    };

    this.scheduleProjectService.projectData
      .asObservable()
      .subscribe((project) => {
        console.log("Project emitted", this.scheduleProjectService);
        if (project && this.scheduleProjectService.initialized) {
          console.log("project data loaded");
          this.projectLoaded = true;
          if (this.schedulerService.scheduler.getValue()) {
            return;
          } else {
            console.log(
              "setting scheduler start / end dates to ",
              this.initStartDate,
              this.initEndDate
            );
            config.startDate = this.initStartDate;
            config.endDate = this.initEndDate;
            config.project = project;
            const mergedConfig = ScheduleDefaults.Create(config, features);
            console.log("scheduler config", config);

            const scheduler = new Scheduler(mergedConfig);
            this.schedulerService.updateScheduler(scheduler);
            this.attachActiveOnlyListeners(scheduler);
          }
          this.schedulerService.scheduler.getValue().project = project;

          this.applyActiveOnlyFilter();

          this.initialized = true;
          this.initialised.emit(true);
        }
      });
  }

  ticketHoverChange(tickets: number[]) {
    let scheduler = this.schedulerService.scheduler.getValue();
    scheduler.clearEventSelection();
    if (tickets.length > 0) {
      let eventsToSelect = [];
      for (let i = 0; i < tickets.length; i++) {
        eventsToSelect.push(
          this.scheduleProjectService.projectData
            .getValue()
            .eventStore.getById(tickets[i])
        );
      }
      scheduler.selectEvents(eventsToSelect);
      if (eventsToSelect && eventsToSelect[0]) {
        scheduler.scrollEventIntoView(eventsToSelect[0] as EventModel);
      }
    }
  }
}
